import { useRouter } from 'next/router';
import { ComponentType, useMemo } from 'react';

import { PageProgress } from '@src/components/common/page-progress';
import { ToastContainer } from '@src/components/molecules/toast-container';
import { HelpFooter } from '@src/components/organisms/help-footer';
import { Navigation } from '@src/components/organisms/navigation';
import { DynamicScripts } from '@src/components/scripts/dynamic';
import { useTrip } from '@src/contexts/trip-context';
import { usePageTracking } from '@src/features/analytics/hooks';
import { AuthProvider } from '@src/features/auth/contexts/auth-context';
import { InstallAppBanner } from '@src/features/install-app-banner';
import { ProfileProvider } from '@src/features/profile/contexts/profile-context';
import { useGuestConsents } from '@src/features/user-consent/hooks';
import { useSetAuthTokenInHttpHeaders } from '@src/hooks/use-auth-token';
import { BrandData, CommonData, TripsData } from '@src/lib/ssr/types';

import { FooterV2 } from './footer';
import { AppWrapper } from './styles';

/**
 * Within a TripContext the main guest can be identified.
 * To not override profile identification, useGuestConsents() is placed in the ProfileContext.
 */
const IdentifyTripMainGuest = () => {
  const trip = useTrip()?.trip;
  const mainTripGuest = trip?.guests.find((guest) => guest.isMainGuest);
  useGuestConsents(mainTripGuest?.email);

  return <></>;
};

export const AppLayout = <T extends Record<string, unknown>>(Component: ComponentType<T>) => {
  const ComponentWithProviders = (props: T & TripsData & BrandData & CommonData) => {
    const { profileCache, isAppWebView, isWhiteLabel, isNativeBrand } = props;
    const { pathname } = useRouter();

    const isAppBannerVisible = useMemo(() => {
      const appBannerVisibilityWhiteList = ['/trips/[id]'];
      const isWhiteListRoute = appBannerVisibilityWhiteList.includes(pathname);
      const isNumaBranded = !isNativeBrand && !isWhiteLabel;

      return isNumaBranded && !isAppWebView && isWhiteListRoute;
    }, [isAppWebView, isNativeBrand, isWhiteLabel, pathname]);

    useSetAuthTokenInHttpHeaders();
    usePageTracking();

    return (
      <>
        <DynamicScripts />

        <AuthProvider>
          <ProfileProvider profileCache={profileCache ?? undefined}>
            <AppWrapper $hasBottomNavigation={!isAppWebView}>
              {isAppBannerVisible && <InstallAppBanner />}
              {!isAppWebView && <Navigation />}

              <IdentifyTripMainGuest />
              <Component {...props} />

              {!isAppWebView && <HelpFooter />}
              {!isAppWebView && <FooterV2 />}

              <PageProgress />

              <ToastContainer />
            </AppWrapper>
          </ProfileProvider>
        </AuthProvider>
      </>
    );
  };
  ComponentWithProviders.displayName = Component.displayName;

  return ComponentWithProviders;
};
